import { useTranslation } from "react-i18next";
import { ReactComponent as AskAiIcon } from "../../assets/icons/ask_ai.svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/copy.svg";
import { ReactComponent as LanguageIcon } from "../../assets/icons/language.svg";
import { ReactComponent as NotesIcon } from "../../assets/icons/notes.svg";
import { ReactComponent as ShareIcon } from "../../assets/icons/share.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as UnderlineIcon } from "../../assets/icons/underline.svg";
import useReaderStore from "../../context/readerStore";
import { toUiLanguage } from "../../i18n/aiLanguages";
import { getAiTargetLanguage } from "../../services/aiService";
import { isFeatureAllowed } from "../../services/drmPolicyService";
import { showComingSoonNotification } from "../../services/notificationService";
import LanguageDropdown from "../common/LanguageDropdown";

/**
 * Shared selection toolbar UI used by desktop (floating) and mobile (bottom-docked).
 * Viewers pass a `selectionToolbar` config; this component only renders controls.
 */
const SelectionToolbar = ({
  toolbar = {},
  isActionRunning = false,
  onAction,
  onClose,
}) => {
  const { t } = useTranslation();
  const drmPolicy = useReaderStore((state) => state.drmPolicy);

  const colors = Array.isArray(toolbar.colors) ? toolbar.colors : [];
  const activeColor = (toolbar.activeColor || "").toUpperCase();
  const actions = toolbar.actions || {};
  const showUnderline = toolbar.canUnderline === true;
  const showHighlightColors = toolbar.canHighlight === true;
  const showDelete = toolbar.canDelete === true && Boolean(toolbar.onDelete);
  const showAskAi =
    Boolean(actions.askAi?.onClick) &&
    isFeatureAllowed(drmPolicy, "ai_features");
  const showCopy = Boolean(actions.copy?.onClick) && !actions.copy?.disabled;
  const showAddNote =
    Boolean(actions.addNote?.onClick) && !actions.addNote?.disabled;
  // Share stays a placeholder; Translate opens LanguageDropdown when wired
  const showShare = true;
  const showTranslate =
    Boolean(actions.translate?.onSelectLanguage) &&
    isFeatureAllowed(drmPolicy, "translation");
  const translateLanguageCode = toUiLanguage(
    actions.translate?.languageCode || getAiTargetLanguage(),
  );
  const showStylesRow = showUnderline || showHighlightColors || showDelete;

  const run = async (handler, { close = true } = {}) => {
    if (!handler || isActionRunning) return;
    await onAction?.(handler);
    if (close) onClose?.();
  };

  return (
    <div className="selection-toolbar">
      {showStylesRow && (
        <div className="selection-toolbar-row selection-toolbar-row--styles">
          {showUnderline && (
            <button
              type="button"
              className={`selection-toolbar-icon-btn${
                toolbar.underlineActive ? " is-active" : ""
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                await run(toolbar.onUnderline);
              }}
              disabled={!toolbar.onUnderline || isActionRunning}
              aria-label={t("contextMenu.underline")}
              title={t("contextMenu.underline")}
              aria-pressed={Boolean(toolbar.underlineActive)}
            >
              <UnderlineIcon
                className="selection-toolbar-style-icon"
                width={16}
                height={16}
                aria-hidden="true"
              />
            </button>
          )}

          {showHighlightColors && (
            <div
              className="selection-toolbar-colors"
              role="listbox"
              aria-label={t(
                "floatingToolbar.highlightColorLabel",
                "Highlight color",
              )}
            >
              {colors.map((opt) => {
                const hex = opt.hex || opt;
                const name = opt.name || hex;
                const isSelected = activeColor === String(hex).toUpperCase();
                return (
                  <button
                    key={hex}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`selection-toolbar-color${
                      isSelected ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: hex }}
                    title={name}
                    aria-label={name}
                    disabled={isActionRunning || !toolbar.onHighlightColor}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await run(() => toolbar.onHighlightColor(hex));
                    }}
                  />
                );
              })}
            </div>
          )}

          {showDelete && (
            <>
              <div className="selection-toolbar-divider" aria-hidden="true" />
              <button
                type="button"
                className="selection-toolbar-icon-btn selection-toolbar-delete"
                onClick={async (e) => {
                  e.stopPropagation();
                  await run(toolbar.onDelete);
                }}
                disabled={isActionRunning}
                aria-label={
                  toolbar.deleteLabel ||
                  t("contextMenu.removeHighlight", "Delete")
                }
                title={
                  toolbar.deleteLabel ||
                  t("contextMenu.removeHighlight", "Delete")
                }
              >
                <TrashIcon
                  className="selection-toolbar-style-icon"
                  width={15}
                  height={15}
                  aria-hidden="true"
                />
              </button>
            </>
          )}
        </div>
      )}

      <div className="selection-toolbar-row selection-toolbar-row--actions">
        {showAskAi && (
          <button
            type="button"
            className="selection-toolbar-action selection-toolbar-action--ask-ai"
            onClick={async (e) => {
              e.stopPropagation();
              await run(actions.askAi.onClick);
            }}
            disabled={isActionRunning}
            aria-label={t("contextMenu.askAi", "Ask AI")}
          >
            <AskAiIcon
              className="preserve-original-fill selection-toolbar-icon-v2"
              aria-hidden="true"
            />
            <span>{t("contextMenu.askAi", "Ask AI")}</span>
          </button>
        )}

        {showCopy && (
          <button
            type="button"
            className="selection-toolbar-action"
            onClick={async (e) => {
              e.stopPropagation();
              await run(actions.copy.onClick);
            }}
            disabled={isActionRunning}
            aria-label={t("contextMenu.copy")}
          >
            <CopyIcon
              className="selection-toolbar-icon-md"
              aria-hidden="true"
            />
            <span>{t("contextMenu.copy")}</span>
          </button>
        )}

        {showAddNote && (
          <button
            type="button"
            className="selection-toolbar-action"
            onClick={async (e) => {
              e.stopPropagation();
              await run(actions.addNote.onClick);
            }}
            disabled={isActionRunning}
            aria-label={t("contextMenu.addNote")}
          >
            <NotesIcon
              className="selection-toolbar-icon-md"
              aria-hidden="true"
            />
            <span>{t("contextMenu.addNote")}</span>
          </button>
        )}

        {showShare && (
          <button
            type="button"
            className="selection-toolbar-action is-placeholder selection-toolbar-action--share"
            onClick={(e) => {
              e.stopPropagation();
              showComingSoonNotification("share");
            }}
            aria-label={t("contextMenu.share", "Share")}
            title={t("contextMenu.comingSoon", "Coming soon")}
          >
            <ShareIcon
              className="selection-toolbar-icon-v2"
              aria-hidden="true"
            />
            <span>{t("contextMenu.share", "Share")}</span>
          </button>
        )}

        {showTranslate && (
          <LanguageDropdown
            className="selection-toolbar-translate-dropdown"
            panelClassName="language-dropdown-panel--selection"
            value={translateLanguageCode}
            align="end"
            ariaLabel={t("contextMenu.translate", "Translate")}
            onChange={(uiCode) => {
              actions.translate.onSelectLanguage(uiCode);
              onClose?.();
            }}
            renderTrigger={({ ref, isOpen, onClick, ...aria }) => (
              <button
                ref={ref}
                type="button"
                className={`selection-toolbar-action selection-toolbar-action--translate${
                  isOpen ? " is-open" : ""
                }`}
                onClick={onClick}
                disabled={isActionRunning}
                {...aria}
              >
                <span className="selection-toolbar-translate-icon">
                  <LanguageIcon
                    className="selection-toolbar-icon-md"
                    aria-hidden="true"
                  />
                  <span
                    className="selection-toolbar-chevron"
                    aria-hidden="true"
                  />
                </span>
                <span>{t("contextMenu.translate", "Translate")}</span>
              </button>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default SelectionToolbar;
